import {
  Conversation,
  ConversationLite,
  FeedbackType,
  Message,
} from '@rocketlane/rocket-llm';

interface PolicyPlan {
  planId: string;
  description: string;
  humanReadableDescription: string;
  inputs: Record<string, any>;
  steps: Record<string, any>[];
  returnValue: string;
  cascadeConflictUpdate: Record<string, any>;
  compiledBy: Record<string, any>;
  triggerCondition: string;
  userMessage?: string;
}

export interface MessageContext {
  policyType?: string;
  referenceId?: string;
  responseType?: 'placeholder' | 'policyPlan' | 'error' | 'clarificationNeeded';
  policyPlan?: PolicyPlan | null;
}

export interface ChatPolicy {
  id: number;
  ref_id: string;
  account_id?: number;
  message_context?: (MessageContext & Record<string, any>) | null;
  message_content?: string | null;
  message_type?: 'human' | 'ai';
  created_at: string;
  updated_at?: string | null;
}

export enum PolicyObjectTypes {
  TimeEntry = 'TIME_ENTRY',
  Project = 'PROJECT',
}

export enum PolicySubType {
  FlagForReview = 'FLAG_FOR_REVIEW',
  AutoReject = 'AUTO_REJECT',
  BlockSubmission = 'BLOCK_SUBMISSION',
  WarnAndProceed = 'WARN_AND_PROCEED',
}

export interface GetChatHistoryParams {
  offset: number;
  limit: number;
  agent_type?: string;
}

export interface AllConversationResponse {
  has_more: boolean;
  page: number;
  page_size: number;
  total: number;
  conversations: ConversationLite[];
}

export type DefaultContentType = {
  type: string;
  category: string;
  title: string | null;
  value: string;
  section_id: number;
  status: 'end' | 'in-progress';
  conversation_id: string;
  message_id: number | null;
  user_message_id: number | null;
};

export type DefaultSectionType = {
  order_index: number;
  type: string;
  title: string | null;
  status: 'completed' | 'in-progress';
  timestamp: string | null;
  content: DefaultContentType[];
};

export type DefaultMessageType = {
  id: number;
  conversation_id: string;
  user_id: number;
  type: 'ai' | 'human' | string;
  sections: DefaultSectionType[];
  feedback: string | null;
  parent_message_id: number | null;
  version: number;
  is_edited: boolean;
  agents_ids: number[];
  metadata: Record<string, any>;
  cost_tracking: any;
  is_streaming: boolean;
  stream_id: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string | null;
};

export interface CreateConversationResponse {
  conversation_id: string;
  success: boolean;
  error_message: string | null;
}

export interface AnalystConfiguration {
  description: string;
}

export interface Analyst {
  id: number;
  name: string;
  plan: string | null;
  initial_prompt: string;
  agent_type: string;
  tools: any | null;
  created_at: string;
  updated_at: string;
  agent_role: string;
  account_id: number;
  is_deleted: boolean;
  created_by: number;
  configuration: AnalystConfiguration;
  agent_inputs: any | null;
  skill_id_arr: number[] | null;
  structured_plan: StructuredPlan | null;
  first_name?: string;
  last_name?: string;
}

export interface ConversationMetadata {
  agent_id?: number;
  agent_name?: string;
  agent_title?: string;
}

export interface SendAnalyticsMessageParams {
  message: string;
  conversationId: string;
  accountId: number;
  userId: number;
  sectionType?: string;
  agent_name?: string;
  conversation_metadata?: ConversationMetadata;
  message_sections?: any[];
}

export interface AnalyticsConversationResponse {
  conversation: {
    conversation_id: string;
    title: string | null;
    user_ids: number[];
    status: string;
    is_deleted: boolean;
    active_version: number;
    metadata: Record<string, any>;
    tags: string[];
    created_at: string;
    updated_at: string | null;
  };
  messages: any[];
  has_more?: boolean;
  page?: number;
  page_size?: number;
  total?: number;
}
export type StructuredPlan = Record<string, any>;

export type IntegrationDispatchChannelType = 'EMAIL';

export interface IntegrationEmailChannelMeta {
  subject: string;
  recipients: { user_id: number }[];
}

export interface IntegrationDispatchChannel {
  channel_type: IntegrationDispatchChannelType;
  channel_meta: IntegrationEmailChannelMeta;
}

export interface IntegrationsDispatchPayload {
  payload: {
    body: string;
    attachment_ids: number[];
  };
  channels: IntegrationDispatchChannel[];
  reference_section_id?: string;
}

export interface SendFeedbackParams {
  messageId: number;
  accountId: number;
  userId: number;
  feedbackType: FeedbackType;
  feedbackCategory?: string;
  feedbackText?: string;
}

export interface GetConversationResponse {
  conversation: Conversation;
  messages: Message[];
  has_more?: boolean;
  page?: number;
  page_size?: number;
  total?: number;
}
